import { useMemo } from "react";

import { CloudAnalyticsModel, type CloudAnalyticsModelAlertModel, Roles } from "@doitintl/cmp-models";
import { type DocumentSnapshotModel, getCollection, type WithModel } from "@doitintl/models-any";
import { useCollectionData } from "@doitintl/models-hooks";

import { useAuthContext } from "../../../../Context/AuthContext";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { useUserContext } from "../../../../Context/UserContext";
import { type AnalyticsAlertWithRef } from "../../../../Pages/CloudAnalytics/alerts";
import { formatFirestoreDate, getOrganizationRef } from "../../../../Pages/CloudAnalytics/utilities";

export const alertsTransform = (
  data: WithModel<CloudAnalyticsModelAlertModel>,
  docSnapshot: DocumentSnapshotModel<CloudAnalyticsModelAlertModel>
): AnalyticsAlertWithRef => ({
  data,
  ref: docSnapshot.ref,
  transform: {
    owner: data.collaborators.find((c) => c.role === Roles.OWNER)?.email,
    lastSent: formatFirestoreDate(data.timeLastAlerted) || null,
    timeCreated: formatFirestoreDate(data.timeCreated),
    timeModified: formatFirestoreDate(data.timeModified),
  },
});

export const useAlerts = (): [AnalyticsAlertWithRef[], boolean] => {
  const { isDoitEmployee, currentUser } = useAuthContext({ mustHaveUser: true });
  const { userRoles } = useUserContext({ requiredRoles: true });
  const { customerOrPresentationModeCustomer: customer, userOrganization } = useCustomerContext();

  const fetchPublicAlerts = useMemo(() => {
    let query = getCollection(CloudAnalyticsModel)
      .doc("alerts")
      .collection("cloudAnalyticsAlerts")
      .where("customer", "==", customer.ref)
      .where("public", "in", [Roles.VIEWER, Roles.EDITOR]);

    // DoiT Employees or Cloud Analytics Admins can see all public alerts from all orgs.
    if (!isDoitEmployee && !userRoles.cloudAnalyticsAdmin) {
      const orgRef = getOrganizationRef(false, userOrganization, customer.id);
      query = query.where("organization", "==", orgRef);
    }

    return query;
  }, [customer.id, customer.ref, isDoitEmployee, userOrganization, userRoles.cloudAnalyticsAdmin]);

  const fetchSharedAlerts = useMemo(() => {
    let query = getCollection(CloudAnalyticsModel)
      .doc("alerts")
      .collection("cloudAnalyticsAlerts")
      .where("customer", "==", customer.ref)
      .where("public", "==", null);

    // DoiT Employees or Cloud Analytics Admins can see all private alerts from all orgs.
    if (!isDoitEmployee && !userRoles.cloudAnalyticsAdmin) {
      const orgRef = getOrganizationRef(false, userOrganization, customer.id);
      query = query
        .where("collaborators", "array-contains-any", [
          { email: currentUser.email, role: Roles.OWNER },
          { email: currentUser.email, role: Roles.VIEWER },
          { email: currentUser.email, role: Roles.EDITOR },
        ])
        .where("organization", "==", orgRef);
    }

    return query;
  }, [userOrganization, customer.id, customer.ref, currentUser.email, isDoitEmployee, userRoles.cloudAnalyticsAdmin]);

  const [publicAlerts, loadingPublicAlerts] = useCollectionData(fetchPublicAlerts, {
    transform: alertsTransform,
  });

  const [sharedAlerts, loadingSharedAlerts] = useCollectionData(fetchSharedAlerts, {
    transform: alertsTransform,
  });

  const alertList = useMemo(() => [...(publicAlerts ?? []), ...(sharedAlerts ?? [])], [publicAlerts, sharedAlerts]);
  const alertsLoading = loadingPublicAlerts || loadingSharedAlerts;

  return [alertList, alertsLoading];
};
